import { DownloadRounded, OpenInFull, TableChartRounded } from '@mui/icons-material'
import GroupIcon from '@mui/icons-material/Group'
import { Grid, ListItemIcon, ListItemText, Menu, MenuItem, useTheme } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useEffect, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { IPaginationQueryResponse } from '../../definitions/Common'
import { IChartData, ICostUtilization, IKpiResponse, IMarginExpense, IRevenueExpense } from '../../definitions/DataAnalytics'
import { ITosExpense } from '../../definitions/ServiceExpense'
import { BarChartResult } from '../charts/BarChartResult'
import { ChartDataWithUnit } from '../charts/ChartsHelper'
import { HorizontalBarChartResult } from '../charts/HorizontalBarChartResult'
import { LineChartResult } from '../charts/LineChartResult'
import { ViewType } from '../contract_tabs/NewContractAnalytics'
import SmartCard from '../controls/cards/SmartCard'
import { KpiCardsResult } from '../controls/data_visualization/KPICardResult'
import LabeledChart from '../controls/data_visualization/LabeledChart'
import useCommand from '../hooks/useCommand'
import useQuery, { buildQueryKey, QueryStatus } from '../hooks/useQuery'
import CustomIcon from '../icons/CustomIcon'
import Tile from '../layout/Tile'
import { snackbar } from '../services/SnackbarService'
import { valueFormatter } from './CostUtilizationView'
interface P {
    contractId: number
    filterStartDate?: string
    filterEndDate?: string
    updateDataFlag: boolean
    changeViewFunction: (view: ViewType) => void
    displayUnderDev: () => void
    handleCsvGeneration: (fileType: string, source?: string, pmpm?: boolean) => void
    setExportMenu: (items: string[]) => void
    viewMode?: string
    setViewMode?: (name: string) => void
}

const slotProps: any = {
    loadingOverlay: {
        variant: 'skeleton',
        noRowsVariant: 'skeleton'
    }
}

const columns: GridColDef[] = [
    { field: 'horizon', headerName: 'Horizon', flex: 1 },
    { field: 'age', headerName: 'Age', flex: 1, renderCell: params => params?.value ?? 'missing' },
    { field: 'tosLevelOne', headerName: 'TOS Level 1', flex: 1, renderCell: params => params?.value ?? 'missing' },
    { field: 'entityTCOC', headerName: 'Entity TCOC', flex: 1, type: 'number', renderCell: params => valueFormatter('currency', params.value) },
    { field: 'entityMemberMonths', headerName: 'Member months', flex: 1, type: 'number', renderCell: params => valueFormatter('currency', params.value) }
]

const NormalView = ({ contractId, filterStartDate, filterEndDate, changeViewFunction, displayUnderDev, updateDataFlag, handleCsvGeneration, setExportMenu, viewMode, setViewMode }: P) => {
    const theme = useTheme()

    const [expenseAnchorElMenu, setExpenseAnchorElMenu] = useState(null)
    const [costUtilizationAnchorElMenu, setCostUtilizationAnchorElMenu] = useState(null)
    const [expenseByTosData, setExpenseByTosData] = useState<{ data: ChartDataWithUnit<'bar', ITosExpense['value'][], string>, status: QueryStatus, refetch?: () => void }>({
        data: {
            datasets: [
                {
                    data: [],
                    unit: '$',
                    backgroundColor: [theme.palette.info.main, theme.palette.primary.light, theme.palette.primary.main, theme.palette.secondary.main, theme.palette.primary.dark]
                }
            ],
            labels: []
        },
        status: 'loading'
    })

    const { response: kpiItems, status: kpiResponseStatus, refetch: kpiRefetch } = useQuery<IKpiResponse>({
        queryKey: buildQueryKey('contractKpi', contractId, filterStartDate, filterEndDate),
        queryFn: async () => {
            const response = await ApiClient.getContractKpiByPeriod(contractId, filterStartDate, filterEndDate)
            return response
        },
        cache: false
    })

    const { response: marginExpenseResponse, status: marginExpenseResponseStatus, refetch: marginExpenseRefetch } = useQuery<IChartData<IMarginExpense>>({
        queryKey: buildQueryKey('marginExpense', contractId, filterStartDate, filterEndDate),
        queryFn: async () => {
            const marginExpenseResponse = await ApiClient.getContractMarginExpenseByPeriod(contractId, filterStartDate, filterEndDate)
            if (marginExpenseResponse) {
                const generateWaterfallColors = (data: number[][]): string[] => {
                    return data.map(([start, end]) => (end - start > 0 ? '#1F6272' : '#BB3646'))
                }
                marginExpenseResponse.datasets[0].backgroundColor = generateWaterfallColors(marginExpenseResponse.datasets[0].data)
                marginExpenseResponse.datasets[0].borderColor = generateWaterfallColors(marginExpenseResponse.datasets[0].data)
            }
            return marginExpenseResponse
        },
        cache: false
    })

    const { status: expenseByTosResponseStatus, refetch: expenseByTosRefetch } = useQuery<ChartDataWithUnit<'bar', ITosExpense['value'][], string>>({
        queryKey: buildQueryKey('expenseByTos', contractId, filterStartDate, filterEndDate),
        queryFn: async () => {
            setViewMode('pmpm')
            let expenseByTosResponse = await ApiClient.getContractTotalExpensePMPMByPeriod(contractId, filterStartDate, filterEndDate)

            if (!expenseByTosResponse) {
                expenseByTosResponse = await ApiClient.getContractTotalExpenseByPeriod(contractId, filterStartDate, filterEndDate)
                setViewMode('default')
            }

            setExpenseByTosData({
                data: {
                    datasets: [
                        {
                            data: expenseByTosResponse.map(item => item.value),
                            unit: '$',
                            backgroundColor: [theme.palette.info.main, theme.palette.primary.light, theme.palette.primary.main, theme.palette.secondary.main, theme.palette.primary.dark]
                        }
                    ],
                    labels: expenseByTosResponse.map(item => item.name)
                },
                status: 'success',
                refetch: expenseByTosRefetch
            })

            return {
                datasets: [
                    {
                        data: expenseByTosResponse.map(item => item.value),
                        unit: '$',
                        backgroundColor: [theme.palette.info.main, theme.palette.primary.light, theme.palette.primary.main, theme.palette.secondary.main, theme.palette.primary.dark]
                    }
                ],
                labels: expenseByTosResponse.map(item => item.name)
            }
        },
        cache: false
    })

    const { execute: loadPMPMExpense } = useCommand({
        commandFn: async (): Promise<ITosExpense[]> => {
            let expenseByTosResponse = []
            setExpenseByTosData(
                prev => ({ ...prev, status: 'loading' })
            )

            switch (viewMode) {
                case 'default':
                    setViewMode('pmpm')
                    expenseByTosResponse = await ApiClient.getContractTotalExpensePMPMByPeriod(contractId, filterStartDate, filterEndDate)
                    break

                case 'pmpm':
                    setViewMode('default')
                    expenseByTosResponse = await ApiClient.getContractTotalExpenseByPeriod(contractId, filterStartDate, filterEndDate)
                    break
            }
            return expenseByTosResponse
        },
        onFinish: result => {
            if (result.response) {
                setExpenseByTosData({
                    data: {
                        datasets: [
                            {
                                data: result.response.map(item => item.value),
                                unit: '$',
                                backgroundColor: [theme.palette.info.main, theme.palette.primary.light, theme.palette.primary.main, theme.palette.secondary.main, theme.palette.primary.dark]
                            }
                        ],
                        labels: result.response.map(item => item.name)
                    },
                    status: 'success'
                })
            }
        }
    })

    const { response: costUtilizationResponse, status: costUtilizationResponseStatus, refetch: costUtilizationRefetch } = useQuery<IPaginationQueryResponse<ICostUtilization>>({
        queryKey: buildQueryKey('smallCostUtilization', contractId, filterStartDate, filterEndDate),
        queryFn: () => ApiClient.getContractCostUtilizationByPeriod(contractId, filterStartDate, filterEndDate, 5, 0),
        cache: false
    })

    const { response: revenueExpenseResponse, status: revenueExpenseResponseStatus, refetch: revenueExpenseRefetch } = useQuery<IChartData<IRevenueExpense>>({
        queryKey: buildQueryKey('revenueExpense', contractId, filterStartDate, filterEndDate),
        queryFn: async () => {
            const revenueExpenseResponse = await ApiClient.getContractRevenueExpenseTrendByPeriod(contractId, filterStartDate, filterEndDate)
            if (revenueExpenseResponse) {
                revenueExpenseResponse.datasets[0].backgroundColor = theme.palette.primary.main
                revenueExpenseResponse.datasets[0].borderColor = theme.palette.primary.main
                revenueExpenseResponse.datasets[1].backgroundColor = theme.palette.secondary.main
                revenueExpenseResponse.datasets[1].borderColor = theme.palette.secondary.main
            }
            return revenueExpenseResponse
        },
        cache: false
    })

    useEffect(() => {
        kpiRefetch()
        revenueExpenseRefetch()
        expenseByTosRefetch()
        marginExpenseRefetch()
        costUtilizationRefetch()
    }, [updateDataFlag])

    if (revenueExpenseResponseStatus === 'error' || kpiResponseStatus === 'error' || marginExpenseResponseStatus === 'error'
        || expenseByTosResponseStatus === 'error' || costUtilizationResponseStatus === 'error') {
        snackbar.show({ severity: 'error', text: 'Error during data fetch, try refreshing the page' })
    }

    const handleChangeView = (view: ViewType) => {
        changeViewFunction(view)
    }

    return (
        <Tile.Container>
            <Tile.Row flex={0} spacing={2}>
                {KpiCardsResult({ data: kpiItems, status: kpiResponseStatus, refetch: kpiRefetch })}
            </Tile.Row>
            <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={6} style={{ maxHeight: '460px' }}>
                    <SmartCard flex={1}>
                        <LabeledChart
                            title='Revenue vs Expense Trend'
                            buttons={[
                                {
                                    icon: <OpenInFull />,
                                    action: () => displayUnderDev()
                                }
                            ]}>
                            <LineChartResult data={revenueExpenseResponse} status={revenueExpenseResponseStatus} theme={theme} refetch={revenueExpenseRefetch} />
                        </LabeledChart>
                    </SmartCard>
                </Grid>
                <Grid item xs={6} style={{ maxHeight: '460px' }}>
                    <SmartCard flex={1}>
                        <LabeledChart
                            title='YTD Margin Expenses'
                            buttons={[
                                {
                                    icon: <OpenInFull />,
                                    action: () => displayUnderDev()
                                }
                            ]}>
                            <BarChartResult<IMarginExpense['data']> data={marginExpenseResponse} status={marginExpenseResponseStatus} theme={theme} refetch={marginExpenseRefetch} />
                        </LabeledChart>
                    </SmartCard>
                </Grid>
                <Grid item xs={6} style={{ maxHeight: '460px' }}>
                    <SmartCard flex={1}>
                        <LabeledChart
                            title='Expense by Type of Service (TOS) - Top 6'
                            chipLabel={viewMode === 'pmpm' ? 'PMPM' : 'Total'}
                            buttons={[
                                {
                                    icon: <DownloadRounded />,
                                    action: e => setExpenseAnchorElMenu(e.currentTarget)
                                },
                                {
                                    icon: viewMode === 'pmpm' ? <GroupIcon /> : <CustomIcon name='pmpm' />,
                                    action: loadPMPMExpense,
                                    tooltip: viewMode === 'pmpm' ? 'Show total expenses view' : 'Show PMPM view'
                                },
                                {
                                    icon: <OpenInFull />,
                                    action: () => {
                                        handleChangeView('expenseByTos')
                                        setExportMenu(['pdf'])
                                    }
                                }
                            ]}>
                            <HorizontalBarChartResult<ITosExpense['value'][]> data={expenseByTosData?.data} status={expenseByTosData?.status} theme={theme} refetch={expenseByTosData?.refetch} />
                        </LabeledChart>
                        { Boolean(expenseAnchorElMenu) && (
                            <Menu
                                anchorEl={expenseAnchorElMenu}
                                open={Boolean(expenseAnchorElMenu)}
                                onClose={() => setExpenseAnchorElMenu(null)}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left'
                                }}
                                slotProps={{ paper: { sx: { marginTop: 0.5 } } }}>
                                <MenuItem onClick={() => {
                                    setExpenseAnchorElMenu(null)
                                    handleCsvGeneration('csv', 'expenseByTos', viewMode === 'pmpm')
                                }}>
                                    <ListItemIcon>
                                        <TableChartRounded />
                                    </ListItemIcon>
                                    <ListItemText>Export as CSV</ListItemText>
                                </MenuItem>
                            </Menu>
                        ) }
                    </SmartCard>
                </Grid>
                <Grid item xs={6} style={{ maxHeight: '460px' }}>
                    <SmartCard flex={1}>
                        <LabeledChart
                            title='Cost Utilization Events / 1000 - Top 5'
                            buttons={[
                                {
                                    icon: <DownloadRounded />,
                                    action: e => setCostUtilizationAnchorElMenu(e.currentTarget)
                                },
                                {
                                    icon: <OpenInFull />,
                                    action: () => {
                                        handleChangeView('costUtilization')
                                        setExportMenu(['csv'])
                                    }
                                }
                            ]}>
                            <DataGrid
                                loading={costUtilizationResponseStatus === 'loading'}
                                rows={costUtilizationResponse?.items ?? []}
                                getRowId={row => row.uuid}
                                columns={columns}
                                sx={{ border: 0 }}
                                hideFooter
                                autosizeOnMount
                                autosizeOptions={{ expand: true }}
                                slotProps={slotProps}
                            />
                        </LabeledChart>
                        { Boolean(costUtilizationAnchorElMenu) && (
                            <Menu
                                anchorEl={costUtilizationAnchorElMenu}
                                open={Boolean(costUtilizationAnchorElMenu)}
                                onClose={() => setCostUtilizationAnchorElMenu(null)}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left'
                                }}
                                slotProps={{ paper: { sx: { marginTop: 0.5 } } }}>
                                <MenuItem onClick={() => {
                                    setCostUtilizationAnchorElMenu(null)
                                    handleCsvGeneration('csv', 'smallCostUtilization')
                                }}>
                                    <ListItemIcon>
                                        <TableChartRounded />
                                    </ListItemIcon>
                                    <ListItemText>Export as CSV</ListItemText>
                                </MenuItem>
                            </Menu>
                        ) }
                    </SmartCard>
                </Grid>
            </Grid>
        </Tile.Container>
    )
}

export default NormalView
